import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import FunctionsDialogFields from "../field/FunctionsDialogFields";
import {InputSwitch} from "primereact/inputswitch";
import {TabPanel, TabView} from "primereact/tabview";
import {
	Application,
	ConfigurationType,
	Portal,
	PortalFunctions,
	PortalUserInterface,
	PortalUserInterfaceLimits
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../../shared/components/Info";

interface FunctionsDialogStatus extends AdminDialogState<Portal> {
	panel: number;
}

export class FunctionsDialogDefinition extends AdminDialog<Portal,
	AdminDialogProperties<Portal>, FunctionsDialogStatus> {
	constructor(props: Readonly<AdminDialogProperties<Portal>>) {
		super(props);
		this.state = {
			...this.state,
			panel: 0
		};
		this.onChange = this.onChange.bind(this);
	}

	public renderGeneralTab(parameter: Portal): ReactNode {
		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-display-diskspace">
						<Translate {...this.props} data="functions.general.displayDiskspace" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalUserInterfaceLimits.getDisplayDiskSpaceDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-display-diskspace"
									 checked={FunctionHelper.applyValue(
										 parameter.userInterface?.limits?.displayDiskSpace,
										 PortalUserInterfaceLimits.getDisplayDiskSpaceDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.DISPLAY_DISKSPACE, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-display-document">
						<Translate {...this.props} data="functions.general.displayDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalUserInterfaceLimits.getDisplayMaxFilesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-display-document"
									 checked={FunctionHelper.applyValue(
										 parameter.userInterface?.limits?.displayMaxFiles,
										 PortalUserInterfaceLimits.getDisplayMaxFilesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.DISPLAY_MAXFILES, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-showHelp">
						<Translate {...this.props} data="functions.general.showHelp" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getShowHelpDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-showHelp"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.showHelp, PortalFunctions.getShowHelpDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.SHOWHELP, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-uploadFile">
						<Translate {...this.props} data="functions.general.uploadFile" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getUploadFileDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-uploadFile"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.uploadFile, PortalFunctions.getUploadFileDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.UPLOADFILE, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-uploadUrl">
						<Translate {...this.props} data="functions.general.uploadUrl" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getUploadUrlDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-uploadUrl"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.uploadUrl, PortalFunctions.getUploadUrlDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.UPLOADURL, e.value
									 )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderPageTab(parameter: Portal): ReactNode {
		return (
			<fieldset className=" p-col-12 p-grid">
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-rotatePages">
						<Translate {...this.props} data="functions.page.rotatePages" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getRotatePagesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-rotatePages"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.rotatePages, PortalFunctions.getRotatePagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.ROTATEPAGES, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-deletePages">
						<Translate {...this.props} data="functions.page.deletePages" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getDeletePagesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-deletePages"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.deletePages, PortalFunctions.getDeletePagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.DELETEPAGES, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-extractPages">
						<Translate {...this.props} data="functions.page.extractPages" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getExtractPagesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-extractPages"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.extractPages, PortalFunctions.getExtractPagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EXTRACTPAGES, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-splitDocument">
						<Translate {...this.props} data="functions.page.splitDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getSplitDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-splitDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.splitDocument, PortalFunctions.getSplitDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.SPLITDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-movePages">
						<Translate {...this.props} data="functions.page.movePages" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getMovePagesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-movePages"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.movePages, PortalFunctions.getMovePagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.MOVEPAGES, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-addwatermark">
						<Translate {...this.props} data="functions.page.addWatermark" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getAddWatermarkDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-addwatermark"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.addWatermark, PortalFunctions.getAddWatermarkDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.ADDWATERMARK, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-extractContent">
						<Translate {...this.props} data="functions.page.extractContent" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getExtractContentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-extractContent"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.extractContent, PortalFunctions.getExtractContentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EXTRACTCONTENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-exportAsImage">
						<Translate {...this.props} data="functions.page.exportAsImage" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getExportAsImageDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-exportAsImage"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.exportAsImage, PortalFunctions.getExportAsImageDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EXPORTASIMAGE, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-createBarcode">
						<Translate {...this.props} data="functions.page.createBarcode" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getCreateBarcodeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-createBarcode"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.createBarcode, PortalFunctions.getCreateBarcodeDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.CREATEBARCODE, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-detectBarcode">
						<Translate {...this.props} data="functions.page.detectBarcode" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getDetectBarcodeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-detectBarcode"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.detectBarcode, PortalFunctions.getDetectBarcodeDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.DETECTBARCODE, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editAttachment">
						<Translate {...this.props} data="functions.page.editAttachment" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditAttachmentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editAttachment"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editAttachment, PortalFunctions.getEditAttachmentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITATTACHMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editAnnotation">
						<Translate {...this.props} data="functions.page.editAnnotation" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditAnnotationDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editAnnotation"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editAnnotation, PortalFunctions.getEditAnnotationDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITANNOTATION, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editTransitions">
						<Translate {...this.props} data="functions.page.editTransitions" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditTransitionsDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editTransitions"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editTransitions,
										 PortalFunctions.getEditTransitionsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITTRANSITIONS, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editBoxes">
						<Translate {...this.props} data="functions.page.editBoxes" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditBoxesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editBoxes"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editBoxes, PortalFunctions.getEditBoxesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITBOXES, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-scalePages">
						<Translate {...this.props} data="functions.page.scalePages" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getScalePagesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-scalePages"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.scalePages, PortalFunctions.getScalePagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.SCALEPAGES, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-transcribe">
						<Translate {...this.props} data="functions.page.transcribe" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getTranscribeDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-transcribe"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.transcribeDocument,
										 PortalFunctions.getTranscribeDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.TRANSCRIBE, e.value
									 )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDocumentTab(parameter: Portal): ReactNode {
		return (
			<fieldset className=" p-col-12 p-grid">
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-renameDocument">
						<Translate {...this.props} data="functions.document.renameDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getRenameDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-renameDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.renameDocument, PortalFunctions.getRenameDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.RENAMEDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-convertDocument">
						<Translate {...this.props} data="functions.document.convertDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getConvertDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-convertDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.convertDocument,
										 PortalFunctions.getConvertDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.CONVERTDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-changeDocumentSettings">
						<Translate {...this.props} data="functions.document.changeDocumentSettings" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getChangeDocumentSettingsDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-changeDocumentSettings"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.changeDocumentSettings,
										 PortalFunctions.getChangeDocumentSettingsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.CHANGEDOCUMENTSETTINGS, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-changeDocumentView">
						<Translate {...this.props} data="functions.document.changeDocumentView" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getChangeDocumentViewDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-changeDocumentView"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.changeDocumentView,
										 PortalFunctions.getChangeDocumentViewDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.CHANGEDOCUMENTVIEW, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editForms">
						<Translate {...this.props} data="functions.document.editForms" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditFormsDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editForms"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editForms, PortalFunctions.getEditFormsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITFORMS, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-encryptDocument">
						<Translate {...this.props} data="functions.document.encryptDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEncryptDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-encryptDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.encryptDocument,
										 PortalFunctions.getEncryptDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.ENCRYPTDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-printDocument">
						<Translate {...this.props} data="functions.document.printDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getPrintDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-printDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.printDocument, PortalFunctions.getPrintDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.PRINTDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-mergeDocument">
						<Translate {...this.props} data="functions.document.mergeDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getMergeDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-mergeDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.mergeDocument, PortalFunctions.getMergeDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.MERGEDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-ocrConversion">
						<Translate {...this.props} data="functions.document.ocrConversion" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getOcrConversionDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-ocrConversion"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.ocrConversion, PortalFunctions.getOcrConversionDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.OCRCONVERSION, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-signDocument">
						<Translate {...this.props} data="functions.document.signDocument" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getSignDocumentDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-signDocument"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.signDocument, PortalFunctions.getSignDocumentDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.SIGNDOCUMENT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-pdfaConversion">
						<Translate {...this.props} data="functions.document.pdfaConversion" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getPdfaConversionDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-pdfaConversion"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.pdfaConversion, PortalFunctions.getPdfaConversionDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.PDFACONVERSION, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editBackground">
						<Translate {...this.props} data="functions.document.editBackground" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditBackgroundDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editBackground"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editBackground, PortalFunctions.getEditBackgroundDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITBACKGROUND, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-editHeaderFooter">
						<Translate {...this.props} data="functions.document.editHeaderFooter" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getEditHeaderFooterDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-editHeaderFooter"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.editHeaderFooter,
										 PortalFunctions.getEditHeaderFooterDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.EDITHEADERFOOTER, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-outline">
						<Translate {...this.props} data="functions.document.outline" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getOutlineDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-outline"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.outline, PortalFunctions.getOutlineDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.OUTLINE, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-portfolio">
						<Translate {...this.props} data="functions.document.portfolio" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getPortfolioDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-portfolio"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.portfolio, PortalFunctions.getPortfolioDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.PORTFOLIO, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-sanitize">
						<Translate {...this.props} data="functions.document.sanitize" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getSanitizeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-sanitize"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.sanitize, PortalFunctions.getSanitizeDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.SANITIZE, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-redact">
						<Translate {...this.props} data="functions.document.redact" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getRedactDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-redact"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.redact, PortalFunctions.getRedactDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.REDACT, e.value
									 )}/>
					</div>
				</div>
				<div className=" p-col-12 p-sm-6 p-grid">
					<label className="p-col-12 p-sm-10" htmlFor="control-compress">
						<Translate {...this.props} data="functions.document.compress" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalFunctions.getCompressDescription()}/>
					</label>
					<div className="p-col-12 p-sm-2 switch-container">
						<InputSwitch inputId="control-compress"
									 checked={FunctionHelper.applyValue(
										 parameter.functions?.compress, PortalFunctions.getCompressDefault()
									 )}
									 onChange={(e) => this.onChange(
										 FunctionsDialogFields.COMPRESS, e.value
									 )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderContents(): ReactNode {
		let parameter: Portal | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<TabView activeIndex={this.state.panel} onTabChange={
				(e) => this.setState({panel: e.index})
			}>
				<TabPanel header={Translate.value("functions.general.label", {ns: "dialog"})}>
					{this.renderGeneralTab(parameter!)}
				</TabPanel>
				<TabPanel header={Translate.value("functions.page.label", {ns: "dialog"})}>
					{this.renderPageTab(parameter!)}
				</TabPanel>
				<TabPanel header={Translate.value("functions.document.label", {ns: "dialog"})}>
					{this.renderDocumentTab(parameter!)}
				</TabPanel>
			</TabView>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["functions-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.functions.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.functions.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-functions"}/>
					<Translate {...this.props} data="functions.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: FunctionsDialogFields, value: any): void {
		let parameter: Portal = this.state.parameter as Portal;

		switch (field) {
			case FunctionsDialogFields.ADDWATERMARK:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.addWatermark = value;
				break;
			case FunctionsDialogFields.CHANGEDOCUMENTSETTINGS:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.changeDocumentSettings = value;
				break;
			case FunctionsDialogFields.CHANGEDOCUMENTVIEW:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.changeDocumentView = value;
				break;
			case FunctionsDialogFields.COMPRESS:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.compress = value;
				break;
			case FunctionsDialogFields.CONVERTDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.convertDocument = value;
				break;
			case FunctionsDialogFields.CREATEBARCODE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.createBarcode = value;
				break;
			case FunctionsDialogFields.DELETEPAGES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.deletePages = value;
				break;
			case FunctionsDialogFields.DETECTBARCODE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.detectBarcode = value;
				break;
			case FunctionsDialogFields.EDITANNOTATION:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editAnnotation = value;
				break;
			case FunctionsDialogFields.EDITATTACHMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editAttachment = value;
				break;
			case FunctionsDialogFields.EDITBACKGROUND:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editBackground = value;
				break;
			case FunctionsDialogFields.EDITBOXES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editBoxes = value;
				break;
			case FunctionsDialogFields.EDITFORMS:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editForms = value;
				break;
			case FunctionsDialogFields.EDITHEADERFOOTER:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editHeaderFooter = value;
				break;
			case FunctionsDialogFields.EDITTRANSITIONS:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.editTransitions = value;
				break;
			case FunctionsDialogFields.ENCRYPTDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.encryptDocument = value;
				break;
			case FunctionsDialogFields.EXPORTASIMAGE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.exportAsImage = value;
				break;
			case FunctionsDialogFields.EXTRACTCONTENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.extractContent = value;
				break;
			case FunctionsDialogFields.EXTRACTPAGES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.extractPages = value;
				break;
			case FunctionsDialogFields.MERGEDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.mergeDocument = value;
				break;
			case FunctionsDialogFields.MOVEPAGES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.movePages = value;
				break;
			case FunctionsDialogFields.OCRCONVERSION:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.ocrConversion = value;
				break;
			case FunctionsDialogFields.OUTLINE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.outline = value;
				break;
			case FunctionsDialogFields.PDFACONVERSION:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.pdfaConversion = value;
				break;
			case FunctionsDialogFields.PORTFOLIO:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.portfolio = value;
				break;
			case FunctionsDialogFields.PRINTDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.printDocument = value;
				break;
			case FunctionsDialogFields.REDACT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.redact = value;
				break;
			case FunctionsDialogFields.RENAMEDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.renameDocument = value;
				break;
			case FunctionsDialogFields.ROTATEPAGES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.rotatePages = value;
				break;
			case FunctionsDialogFields.SANITIZE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.sanitize = value;
				break;
			case FunctionsDialogFields.SCALEPAGES:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.scalePages = value;
				break;
			case FunctionsDialogFields.SHOWHELP:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.showHelp = value;
				break;
			case FunctionsDialogFields.SIGNDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.signDocument = value;
				break;
			case FunctionsDialogFields.SPLITDOCUMENT:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.splitDocument = value;
				break;
			case FunctionsDialogFields.UPLOADFILE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.uploadFile = value;
				break;
			case FunctionsDialogFields.UPLOADURL:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.uploadUrl = value;
				break;
			case FunctionsDialogFields.TRANSCRIBE:
				if (!FunctionHelper.exists(parameter.functions)) {
					parameter.functions = new PortalFunctions({});
				}

				parameter.functions!.transcribeDocument = value;
				break;
			case FunctionsDialogFields.DISPLAY_DISKSPACE:
				if (!FunctionHelper.exists(parameter.userInterface)) {
					parameter.userInterface = new PortalUserInterface({});
				}

				if (!FunctionHelper.exists(parameter.userInterface?.limits)) {
					parameter.userInterface!.limits = new PortalUserInterfaceLimits({});
				}

				parameter.userInterface!.limits!.displayDiskSpace = value;
				break;
			case FunctionsDialogFields.DISPLAY_MAXFILES:
				if (!FunctionHelper.exists(parameter.userInterface)) {
					parameter.userInterface = new PortalUserInterface({});
				}

				if (!FunctionHelper.exists(parameter.userInterface?.limits)) {
					parameter.userInterface!.limits = new PortalUserInterfaceLimits({});
				}

				parameter.userInterface!.limits!.displayMaxFiles = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.portal = this.state.parameter!;

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.functions", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.FUNCTIONS;
	}
}

const FunctionsDialog = React.memo(FunctionsDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<Portal>>,
		nextProps: Readonly<AdminDialogProperties<Portal>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default FunctionsDialog;